import { Navigate, useLocation, useOutlet, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { NavMain } from "./NavMain";
import { Provider } from "../context/appContext"
import NavTop from "./NavTop";
import { firebaseConfig, auth } from "../firebase";
import { ActivationRequired } from "./ActivationRequired";
import { LinearProgress, Box, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { signInWithCustomToken } from "firebase/auth";

export const ProtectedLayout = () => {

    const { user, claims, token } = useAuth();
    const outlet = useOutlet();
    const navigate = useNavigate();
    const location = useLocation();
    const [punchoutAuthInProgress, setPunchoutAuthInProgress] = useState(false);
    const [punchoutAuthAttempted, setPunchoutAuthAttempted] = useState(false);
    const [punchoutAuthError, setPunchoutAuthError] = useState(false);

    const isPunchoutRoute = location.pathname.startsWith('/web/punchout');
    const locationParams = new URLSearchParams(location.search);
    const punchoutTokenInUrl = locationParams.get('token');
    const isPunchoutTokenFlow = isPunchoutRoute && !!punchoutTokenInUrl;
    const isPunchoutAuthPending = isPunchoutTokenFlow && !user && !punchoutAuthAttempted;
    const isPunchoutTransferInProgress = sessionStorage.getItem('punchoutTransferInProgress') === '1';

    useEffect(() => {
        // Remove stale transfer marker when user is no longer on a punchout route.
        if (!isPunchoutRoute && isPunchoutTransferInProgress) {
            sessionStorage.removeItem('punchoutTransferInProgress');
        }
    }, [isPunchoutRoute, isPunchoutTransferInProgress]);

    // Handle punchout token-based authentication
    useEffect(() => {
        const handlePunchoutAuth = async () => {
            const urlParams = new URLSearchParams(location.search);
            const punchoutToken = urlParams.get('token');
            const sessionId = urlParams.get('sessionId');
            const punchoutType = urlParams.get('punchoutType');
            
            // If we have a punchout token and no user, attempt auto-sign-in
            if (!user && isPunchoutTokenFlow && !punchoutAuthAttempted) {
                console.log('🔐 Punchout token detected, attempting auto-sign-in...');
                setPunchoutAuthInProgress(true);
                setPunchoutAuthAttempted(true);
                setPunchoutAuthError(false);
                
                try {
                    await signInWithCustomToken(auth, punchoutToken);
                    console.log('✅ Punchout auto-sign-in successful');
                    
                    // Store punchout session info in sessionStorage for the punchout cart
                    if (sessionId) {
                        sessionStorage.setItem('punchoutSessionId', sessionId);
                    }
                    if (punchoutType) {
                        sessionStorage.setItem('punchoutType', punchoutType);
                    }
                    
                    // Build clean punchout URL without the token (security)
                    const cleanParams = new URLSearchParams();
                    if (sessionId) cleanParams.set('sessionId', sessionId);
                    if (punchoutType) cleanParams.set('punchoutType', punchoutType);
                    const returnUrl = urlParams.get('returnUrl');
                    if (returnUrl) cleanParams.set('returnUrl', returnUrl);
                    
                    // Stay on punchout page with session parameters (without token)
                    navigate(`/web/punchout?${cleanParams.toString()}`);
                    
                } catch (error) {
                    console.error('❌ Punchout auto-sign-in failed:', error.message);
                    // Fall back to regular login flow
                    setPunchoutAuthError(true);
                    setPunchoutAuthInProgress(false);
                }
            }
        };
        
        handlePunchoutAuth();
    }, [user, punchoutAuthAttempted, isPunchoutTokenFlow, location.search, navigate]);

    // Clear punchout auth loading state when user becomes available after auth attempt
    useEffect(() => {
        if (user && punchoutAuthAttempted && punchoutAuthInProgress) {
            console.log('✅ User authenticated, clearing punchout auth loading state');
            setPunchoutAuthInProgress(false);
        }
    }, [user, punchoutAuthAttempted, punchoutAuthInProgress]);

    // Handle punchout parameter restoration after login
    useEffect(() => {
        if (user && claims && token) {
            const punchoutParams = sessionStorage.getItem('punchoutParams');
            if (punchoutParams) {
                console.log('🔄 Restoring punchout parameters after login:', punchoutParams);
                sessionStorage.removeItem('punchoutParams');
                
                // Parse and store punchout session info
                const params = new URLSearchParams(punchoutParams);
                const sessionId = params.get('sessionId');
                const punchoutType = params.get('punchoutType');
                
                if (sessionId) {
                    sessionStorage.setItem('punchoutSessionId', sessionId);
                }
                if (punchoutType) {
                    sessionStorage.setItem('punchoutType', punchoutType);
                }
                
                // Redirect to punchout page with stored parameters (without token)
                const cleanParams = new URLSearchParams();
                if (sessionId) cleanParams.set('sessionId', sessionId);
                if (punchoutType) cleanParams.set('punchoutType', punchoutType);
                const returnUrl = params.get('returnUrl');
                if (returnUrl) cleanParams.set('returnUrl', returnUrl);
                
                navigate(`/web/punchout?${cleanParams.toString()}`);
                return;
            }
        }
    }, [user, claims, token, navigate]);

    // Keep users on a neutral loading screen while punchout auth is unresolved.
    // This prevents a visible flash of the /login page during token-based sign-in.
    if (punchoutAuthInProgress || isPunchoutAuthPending) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
                <LinearProgress sx={{ width: '200px', mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                    Authentifizierung läuft...
                </Typography>
            </Box>
        );
    }

    if (!user) {
        // During cart handoff we intentionally keep a neutral loader to avoid a login-page flash.
        if (isPunchoutRoute && isPunchoutTransferInProgress) {
            return (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
                    <LinearProgress sx={{ width: '200px', mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                        Warenkorb wird an das Beschaffungssystem uebertragen...
                    </Typography>
                </Box>
            );
        }

        // If punchout token auth already failed, continue with normal login redirect.
        if (punchoutAuthError && isPunchoutRoute) {
            console.warn('Punchout auto-auth failed, redirecting to manual login');
        }
        
        // Check if this is a punchout request without token - preserve parameters for manual login
        if (isPunchoutRoute && window.location.search) {
            // Store punchout parameters in sessionStorage for after login
            const punchoutParams = window.location.search;
            sessionStorage.setItem('punchoutParams', punchoutParams);
            console.log('🔗 Storing punchout parameters for post-login redirect:', punchoutParams);
        }
        return <Navigate to="/login" />;
    }

    // Wait for claims to load before making decisions
    // If user exists but claims/token are not loaded yet, show loading
    if (user && (!claims || !token)) {
        console.log("Waiting for claims to load...", { user: !!user, claims: !!claims, token: !!token });
        return <LinearProgress />;
    }

    // For new users (role is Inactive or missing): also require email verification.
    // Existing active users may have emailVerified=false (registered before this flow)
    // and must not be blocked.
    const isInactive = claims && (claims.role === 'Inactive' || !claims.role);

    if (isInactive && user && !user.emailVerified) {
        console.log("New user email is not verified yet");
        return <ActivationRequired reason="emailVerification" />;
    }

    // Check if user is inactive (needs admin activation) or has no role set yet
    if (isInactive) {
        console.log("User is inactive or has no role, showing activation screen");
        return <ActivationRequired reason="adminApproval" />;
    }

    // Check if user is in a punchout session (route or stored session id)
    const isPunchoutSession =
        isPunchoutRoute || !!sessionStorage.getItem('punchoutSessionId');

    // Define pages array with conditional Meta-Suche and data-import
    const pages = [
        { label: "Home", path: "welcome" },
        // { label: "Schnellsuche", path: "searchlibriapi" },
        // { label: "Profisuche", path: "searchvlbapi" },
        // { label: "Libri Suche", path: "searchlibri" },
        // { label: "Zeitschriften Suche", path: "searchmagazines" },

        // Hide status pages for Buyer role
        ...(claims?.role !== "Buyer" ? [
            { label: "Bestell-Status", path: "orderstatus" },
            { label: "Abo-Status", path: "abonnements/abostatus" },
            ...(firebaseConfig.projectId === "litport-schoeningh" ? [{ label: "Rechnungsarchiv", path: "rechnungsarchiv" }] : []),
        ] : []),
        
        // Hide Bestell-Service for punchout users (they must use punchout cart)
        ...(!isPunchoutSession ? [
            { label: firebaseConfig.projectId === "litport-schoeningh" ? "Freitext-Bestellung" : "Bestell-Service", path: "orderservice" },
        ] : []),
        ...(!isPunchoutSession && firebaseConfig.projectId === "litport-schoeningh"
            ? [{ label: "Webshop", path: "https://fachmedien.schoeningh-buch.de/", external: true }]
            : []),
    ];

    // Add data-import only for Super Admins in Schöningh
    console.log("ProtectedLayout Debug:", {
        projectId: firebaseConfig.projectId,
        superuser: claims?.superuser,
        claims: claims
    });
    
    // Primary Meta-Suche (Typesense) is available to all users.
    pages.splice(1, 0, { label: "Meta-Suche", path: "searchmeta" });

    // Legacy Elastic Meta-Suche — disabled
    // const isAdminOrSuper = claims?.role === "Admin" || claims?.role === "SuperAdmin";
    // if (isAdminOrSuper) {
    //     const insertIndex = 2;
    //     pages.splice(insertIndex, 0, { label: "Meta-Suche (alt)", path: "meta-legacy" });
    // }

    return (

        <Provider>
            <NavTop></NavTop>
            <NavMain
                pages={pages}
                settings={[
                    { label: "Profil", path: "profile" },
                ]}
            />
            {outlet}
        </Provider>

    );
};